import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Card, Col, InputNumber, Row, Space, Typography, message } from "antd";

import { BodyMeasurement, MeasurementType } from "@fitness-app/data-models/entities/Measurement";

import { type SimulatorTabProps } from "../types";
import {
  buildBodyMeasurementData,
  buildStepsData,
  SIMULATOR_BODY_MEASUREMENT_KEYS,
  writeMeasurement,
  type SimulatorMeasurementType,
} from "../writes/measurementWrites";

const BODY_MEASUREMENT_LABEL: Record<BodyMeasurement, string> = {
  [BodyMeasurement.BODY_FAT]: "Tkanka tłuszczowa",
  [BodyMeasurement.BICEPS_LEFT]: "Biceps lewy",
  [BodyMeasurement.BICEPS_RIGHT]: "Biceps prawy",
  [BodyMeasurement.CALF_LEFT]: "Łydka lewa",
  [BodyMeasurement.CALF_RIGHT]: "Łydka prawa",
  [BodyMeasurement.CHEST]: "Klatka",
  [BodyMeasurement.HIPS]: "Biodra",
  [BodyMeasurement.MIDDLE]: "Talia (środek)",
  [BodyMeasurement.NECK]: "Szyja",
  [BodyMeasurement.THIGH_LEFT]: "Udo lewe",
  [BodyMeasurement.THIGH_RIGHT]: "Udo prawe",
  [BodyMeasurement.WAIST]: "Pas",
};

const SimulatorMeasurementsTab = ({ target, date, createdBy, onWritten }: SimulatorTabProps) => {
  const [weight, setWeight] = useState<number | null>(65);
  const [portion, setPortion] = useState<number | null>(2000);
  const [steps, setSteps] = useState<number | null>(8000);
  const [bodyValues, setBodyValues] = useState<Partial<Record<BodyMeasurement, number | null>>>({});

  const save = useMutation({
    mutationFn: ({
      type,
      data,
    }: {
      type: SimulatorMeasurementType;
      data: Parameters<typeof writeMeasurement>[0]["data"];
    }) =>
      writeMeasurement({
        traineeId: target.id,
        userId: target.userId,
        createdBy,
        date,
        mode: "single",
        type,
        data,
      }),
    onSuccess: () => {
      void message.success("Pomiar zapisany");
      onWritten();
    },
    onError: (error: Error) => void message.error(error.message),
  });

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Alert
        type="info"
        showIcon
        message="Pomiar tego samego typu w tym samym dniu nadpisuje poprzedni"
        description="Tak samo zachowuje się aplikacja klientki — drugi wpis tego dnia nie tworzy nowego pomiaru, tylko zmienia poprzedni."
      />

      <Card size="small" title="Waga">
        <Space>
          <InputNumber value={weight} onChange={setWeight} min={20} max={300} step={0.1} addonAfter="kg" />
          <Button
            type="primary"
            disabled={weight === null}
            loading={save.isLoading}
            onClick={() => save.mutate({ type: MeasurementType.WeightMeasurement, data: { weight: weight! } })}
          >
            Zapisz wagę
          </Button>
        </Space>
      </Card>

      <Card size="small" title="Obwody">
        <Space direction="vertical" size={12} className="w-full">
          <Row gutter={[12, 12]}>
            {SIMULATOR_BODY_MEASUREMENT_KEYS.map((key) => (
              <Col xs={12} md={6} key={key}>
                <Typography.Text type="secondary">{BODY_MEASUREMENT_LABEL[key]}</Typography.Text>
                <InputNumber
                  className="w-full"
                  min={10}
                  max={250}
                  step={0.5}
                  addonAfter="cm"
                  value={bodyValues[key] ?? null}
                  onChange={(value) => setBodyValues((current) => ({ ...current, [key]: value }))}
                />
              </Col>
            ))}
          </Row>
          <Button
            type="primary"
            disabled={SIMULATOR_BODY_MEASUREMENT_KEYS.every((key) => bodyValues[key] == null)}
            loading={save.isLoading}
            onClick={() =>
              save.mutate({
                type: MeasurementType.BodyMeasurements,
                data: buildBodyMeasurementData(bodyValues),
              })
            }
          >
            Zapisz obwody
          </Button>
        </Space>
      </Card>

      <Card size="small" title="Nawodnienie">
        <Space direction="vertical" size={8} className="w-full">
          <Space>
            <InputNumber value={portion} onChange={setPortion} min={0} max={8000} step={100} addonAfter="ml" />
            <Button
              type="primary"
              disabled={portion === null}
              loading={save.isLoading}
              onClick={() => save.mutate({ type: MeasurementType.HydrationMeasurement, data: { portion: portion! } })}
            >
              Zapisz nawodnienie
            </Button>
          </Space>
          <Typography.Text type="secondary">
            To jest stan licznika na koniec dnia w mililitrach, a nie kolejny łyk — klientka podbija go co 100 ml, a
            kategoria `hydration` w wyzwaniach bierze największą wartość z dnia, nie sumę zdarzeń.
          </Typography.Text>
        </Space>
      </Card>

      <Card size="small" title="Kroki">
        <Space direction="vertical" size={8} className="w-full">
          <Space>
            <InputNumber value={steps} onChange={setSteps} min={0} max={100000} step={500} addonAfter="kroków" />
            <Button
              type="primary"
              disabled={steps === null}
              loading={save.isLoading}
              onClick={() =>
                save.mutate({
                  type: MeasurementType.HealthStepsMeasurement,
                  data: buildStepsData(steps!, date),
                })
              }
            >
              Zapisz kroki
            </Button>
          </Space>
          <Typography.Text type="secondary">
            Zapisywane jako synchronizacja z Health Connect — tak samo, jak trafiają z telefonu klientki.
          </Typography.Text>
        </Space>
      </Card>
    </Space>
  );
};

export default SimulatorMeasurementsTab;
